import type { FC } from 'react'
import { createContext, useState, useContext, useEffect } from 'react'
// eslint-disable-next-line import/no-extraneous-dependencies
import type { RouteConfig } from 'react-router-config'
import { matchRoutes } from 'react-router-config'
import type { History } from 'history-with-query'

interface IRouteMetaProps {
  routes: Route[]
  history: History
}

interface IRouteMetaContext {
  matchedRoutes?: Route[]
  currentRoute?: Route
}

const RouteMetaContext = createContext<IRouteMetaContext | null>(null)

const getRouteMeta: (routes: Route[]) => [Route[], Route] = (routes) => {
  const matched = matchRoutes(routes as RouteConfig[], window.location.pathname).map(
    (matchedROute) => {
      const route = { ...matchedROute.route }
      Reflect.deleteProperty(route, 'component')
      Reflect.deleteProperty(route, 'wrappers')
      return route
    },
  ) as Route[]
  const current = matched[matched.length - 1]
  return [matched, current]
}

const RouteMeta: FC<IRouteMetaProps> = ({ routes, history, children }) => {
  const [[matchedRoutes, currentRoute], setRouteMeta] = useState<[Route[], Route]>(() => {
    return getRouteMeta(routes)
  })

  useEffect(() => {
    const unListen = history.listen(() => {
      setRouteMeta(getRouteMeta(routes))
    })
    return () => {
      unListen()
    }
  }, [])

  return (
    <RouteMetaContext.Provider value={{ matchedRoutes, currentRoute }}>
      {children}
    </RouteMetaContext.Provider>
  )
}

const useRouteMeta = () => {
  return useContext(RouteMetaContext)
}

export { useRouteMeta }

export default RouteMeta
